import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setLanguage } from '../store/slices/preferencesSlice';
import { analyticsService } from '../services/analyticsService';
import { SUPPORTED_LANGUAGES } from '../i18n/config';
import logger from '../utils/logger';

export const useLanguage = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const [isChanging, setIsChanging] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0); // Used to force re-renders
  const currentLanguage = useSelector((state: RootState) => state.preferences.language);
  const isInitialMount = useRef(true);
  const lastLanguageChange = useRef<string | null>(null);

  // Helper function to get language code without region
  const getLanguageCode = (lang: string): string => {
    return lang.split('-')[0];
  };

  // Debug log for initial state
  useEffect(() => {
    logger.debug('useLanguage - Initial Redux language:', currentLanguage);
    logger.debug('useLanguage - Initial i18n language:', i18n.language);
    logger.debug('useLanguage - Browser language:', navigator.language);
    logger.debug('useLanguage - Browser language code:', getLanguageCode(navigator.language));
  }, []);

  const changeLanguage = useCallback(async (language: string) => {
    // Normalize language code (remove region if present)
    const normalizedLanguage = getLanguageCode(language);
    
    // Prevent duplicate language changes
    if (lastLanguageChange.current === normalizedLanguage) {
      logger.debug('useLanguage - Duplicate language change prevented:', normalizedLanguage);
      return;
    }

    if (!Object.keys(SUPPORTED_LANGUAGES).includes(normalizedLanguage)) {
      logger.error('useLanguage - Unsupported language:', normalizedLanguage);
      throw new Error(`Unsupported language: ${normalizedLanguage}`);
    }

    try {
      logger.debug('useLanguage - Starting language change to:', normalizedLanguage);
      logger.debug('useLanguage - Current Redux language:', currentLanguage);
      logger.debug('useLanguage - Current i18n language:', i18n.language);
      
      setIsChanging(true);
      lastLanguageChange.current = normalizedLanguage;

      // Load translations if not already loaded
      await i18n.loadNamespaces(['translation']);
      
      // Change language
      await i18n.changeLanguage(normalizedLanguage);
      logger.debug('useLanguage - i18n language changed to:', i18n.language);
      
      // Update Redux store only if it's different
      if (getLanguageCode(currentLanguage) !== normalizedLanguage) {
        logger.debug(`useLanguage - Updating Redux store language from ${currentLanguage} to ${normalizedLanguage}`);
        dispatch(setLanguage(normalizedLanguage));
      }
      
      // Track analytics
      analyticsService.trackEvent(analyticsService.events.LANGUAGE_CHANGED, {
        language: normalizedLanguage,
        source: 'user_selection'
      });

      // Store language preference in localStorage
      localStorage.setItem('i18nextLng', normalizedLanguage);
      logger.debug('useLanguage - Updated localStorage i18nextLng to:', normalizedLanguage);
      
      // Force a re-render
      setForceUpdate(prev => prev + 1);
      logger.debug('useLanguage - Language change complete');

    } catch (error) {
      logger.error('Language change failed:', error);
      analyticsService.trackEvent('language_change_failed', {
        language: normalizedLanguage,
        error: String(error)
      });
      throw error;
    } finally {
      setIsChanging(false);
    }
  }, [i18n, dispatch, currentLanguage]);

  // Listen for language changes from outside this hook
  useEffect(() => {
    const handleLanguageChanged = () => {
      logger.debug('useLanguage - External language change detected, i18n language:', i18n.language);
      logger.debug('useLanguage - Current Redux language:', currentLanguage);
      setForceUpdate(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n, currentLanguage]);

  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        logger.debug('useLanguage - Initializing language');
        logger.debug('useLanguage - isInitialMount:', isInitialMount.current);
        logger.debug('useLanguage - currentLanguage:', currentLanguage);
        logger.debug('useLanguage - i18n.language:', i18n.language);
        
        // Skip on initial mount
        if (isInitialMount.current) {
          isInitialMount.current = false;
          logger.debug('useLanguage - Skipping initialization on first mount');
          return;
        }

        // If no language is set, detect browser language
        if (!currentLanguage) {
          const browserLang = getLanguageCode(navigator.language);
          logger.debug('useLanguage - No language set, detecting browser language:', browserLang);
          
          const supportedLang = Object.keys(SUPPORTED_LANGUAGES).includes(browserLang)
            ? browserLang
            : 'en';
          
          logger.debug('useLanguage - Using detected language:', supportedLang);
          await changeLanguage(supportedLang);
        } else if (getLanguageCode(i18n.language) !== getLanguageCode(currentLanguage) && 
                  lastLanguageChange.current !== getLanguageCode(currentLanguage)) {
          // Ensure i18n language matches Redux store
          logger.debug('useLanguage - Syncing i18n language with Redux store');
          logger.debug('useLanguage - i18n language:', i18n.language);
          logger.debug('useLanguage - Redux language:', currentLanguage);
          await changeLanguage(currentLanguage);
        }
      } catch (error) {
        logger.error('Language initialization failed:', error);
      }
    };

    initializeLanguage();
  }, [currentLanguage, i18n.language, changeLanguage]);

  return {
    currentLanguage: getLanguageCode(currentLanguage || i18n.language),
    isChanging,
    changeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    forceUpdate // Include this in the return value to force re-renders
  };
}; 